import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  PLAYLISTS_INITIAL_STATE,
  PlaylistsState
} from './playlists.model';
import { CallStateEnum } from '@shared/models/call-state.model';
import { SnackbarType } from '@shared/models/app-snackbar.model';
import useSnackbarStore from '@store/snackbar-store/snackbar.store';
import { withCallState } from '@shared/store-features/call-state-feature';
import {
  createPlaylist as createPlaylistApi,
  deletePlaylist,
  deletePlaylistsMany,
  editPlaylist as editPlaylistApi,
  getScreensList
} from '@shared/api/digital-signage/playlists-api.ts';

const { showSnackbar } = useSnackbarStore.getState();

const usePlaylistsStore = create<PlaylistsState>()(
  devtools(
    (set, getState) => ({
      ...PLAYLISTS_INITIAL_STATE,
      ...withCallState(getState),

      getScreens: async () => {
        try {
          const data = await getScreensList();
          const screensData = data.map((item) => ({
            id: item._id,
            title: item.title,
            value: item.title
          }));
          set({ screensData });
        } catch (error) {
          if (error instanceof Error) {
            showSnackbar(error.message, SnackbarType.Error);
          }
        }
      },

      deleteRow: async ({ ids, state, handleClose }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          if (ids.length === 1) {
            await deletePlaylist(ids[0]);
          } else {
            await deletePlaylistsMany(ids);
          }
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(
            'snackbar_delete_digital_signage_playlist_text',
            SnackbarType.Success
          );
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (error instanceof Error) {
            showSnackbar(error.message, SnackbarType.Error);
          }
        }
      },

      createPlaylist: async ({ body, state, handleClose }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          const createdPlaylist = await createPlaylistApi(body);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state?.setApiFunctionAndFetchData?.(state.apiFunction);
          showSnackbar(
            'snackbar_success_digital_signage_playlist_text',
            SnackbarType.Success
          );

          return createdPlaylist;
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (error instanceof Error) {
            showSnackbar(error.message, SnackbarType.Error);
          }
        }
      },

      editPlaylist: async ({ id, body, state, handleClose }) => {
        set({ callState: CallStateEnum.Loading });
        try {
          await editPlaylistApi(id, body);
          set({ callState: CallStateEnum.Loaded });
          handleClose?.();
          state.setApiFunctionAndFetchData(state.apiFunction);
          showSnackbar(
            'snackbar_update_digital_signage_playlist_text',
            SnackbarType.Success
          );
        } catch (error) {
          set({ callState: CallStateEnum.Error });
          if (error instanceof Error) {
            showSnackbar(error.message, SnackbarType.Error);
          }
        }
      }
    }),

    { name: 'DigitalSignagePlaylistsStore' }
  )
);

export default usePlaylistsStore;
